/**
 * Deferred Sentry boot (QW4 in specs/bundle-optimization.md).
 *
 * `@sentry/react` is loaded behind a dynamic import on idle so the SDK is never
 * part of the eager graph of any page (previously it was statically imported by
 * the entry chunk and paid for by anonymous visitors on the login page).
 *
 * Errors that happen before the SDK finishes booting must not be lost: the
 * boot helper installs temporary global handlers and the local error boundaries
 * (`AppErrorBoundary`) forward everything thrown to `captureError`, which
 * queues pre-boot errors and replays them to Sentry once it is ready.
 */

const PRE_BOOT_QUEUE_LIMIT = 50;

const preBootErrors: unknown[] = [];
let bootStarted = false;
let sentryReady = false;

const dsn = import.meta.env.VITE_SENTRY_DSN;

const captureWithSentry = (error: unknown) => {
  void import("@sentry/react")
    .then((Sentry) => {
      Sentry.captureException(error);
    })
    .catch(() => {
      // Sentry failed to load — nothing else we can do here.
    });
};

const onError = (event: ErrorEvent) => {
  captureError(event.error ?? event.message);
};

const onUnhandledRejection = (event: PromiseRejectionEvent) => {
  captureError(event.reason);
};

/**
 * Queue an error for reporting. If Sentry is already booted the error is
 * captured straight away; otherwise it is queued and replayed on boot.
 */
export function captureError(error: unknown): void {
  if (!dsn) return;
  if (sentryReady) {
    captureWithSentry(error);
  } else if (preBootErrors.length < PRE_BOOT_QUEUE_LIMIT) {
    preBootErrors.push(error);
  }
}

/**
 * Start loading Sentry on idle. Safe to call multiple times; no-op when no DSN
 * is configured (e.g. local dev / e2e builds without Sentry).
 */
export function bootSentry(): void {
  if (!dsn || bootStarted) return;
  bootStarted = true;

  // Temporary handlers catch non-React errors thrown before Sentry installs
  // its own global handlers on init.
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  const onIdle = (cb: () => void) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => cb(), { timeout: 3000 });
    } else {
      setTimeout(cb, 1);
    }
  };

  onIdle(() => {
    void import("@sentry/react")
      .then((Sentry) => {
        Sentry.init({
          dsn,
          environment: import.meta.env.VITE_HOST_ENVIRONMENT,
          ignoreErrors: [/ChunkLoadError/],
        });
        // Stop intercepting globals — Sentry's own handlers take over.
        window.removeEventListener("error", onError);
        window.removeEventListener("unhandledrejection", onUnhandledRejection);
        sentryReady = true;
        // Replay anything that threw before the SDK was ready.
        while (preBootErrors.length > 0) {
          captureWithSentry(preBootErrors.shift());
        }
      })
      .catch(() => {
        // Sentry failed to load; keep the queue in case of a retry.
        bootStarted = false;
      });
  });
}
